import { Layout, notification, Spin } from "antd"
import { withRouter, StaticContext, RouteComponentProps } from "react-router"
import usePlatform from "contexts/platform/use-platform"
import { IUpdatePolicyholder } from "platform-client/client/controllers/policyholders"
import { IPolicyholder } from "platform-client/types"
import React from "react"
import { useQuery, useMutation } from "react-query"
import ClientForm, { IClientData } from "components/Client/ClientForm"
import { LayoutPageContent } from "components"

const EditClient: React.FC<IUpdateClientProps> = (props) => {
  const clientId = props?.match?.params?.clientId
  const goBack = () => {
    const params = new URLSearchParams(props.location.search)

    if (params.has("url")) {
      const url = params.get("url") || ""
      return props.history.push(url)
    }

    return props.history.goBack()
  }

  const [client, setClient] = React.useState<IPolicyholder | undefined>(
    undefined
  )
  const platform = usePlatform()

  const clientData = useQuery(
    ["client", clientId],
    () => platform.policyholders.getPolicyholder(clientId),
    {
      onSuccess: (response) => setClient(response),
      onError: () => {
        notification.error({
          message: "Something went wrong, please try again later"
        })
        goBack()
      }
    }
  )

  const updatePolicyholder = useMutation(
    (updatePolicyholderRequest: IUpdatePolicyholder) =>
      platform.policyholders.updatePolicyholder(
        clientId,
        updatePolicyholderRequest
      )
  )
  const onFinish = async (formData: IClientData) => {
    if (formData.policyholderType) {
      // Construct legalName from name fields for Individual, or use businessName for Business
      let legalName: string
      if (formData.policyholderType === "Business") {
        legalName = formData.businessName || ""
      } else {
        const nameParts = [
          formData.title,
          formData.firstName,
          formData.lastName
        ].filter(Boolean)
        legalName = nameParts.join(" ").trim()
      }

      if (!legalName) {
        notification.error({
          message: "Unable to submit form, missing required fields."
        })
        return
      }

      updatePolicyholder
        .mutateAsync({
          businessName: formData.businessName || null,
          companyID: client?.companyID ?? null,
          contact: {
            name: {
              firstName: formData.firstName || null,
              lastName: formData.lastName || null,
              title: formData.title || null
            },
            dateOfBirth: formData.dateOfBirth
              ? formData.dateOfBirth.format("YYYY-MM-DD")
              : null,
            phoneNumber: formData.phoneNumber || null,
            emailAddress: formData.emailAddress || null
          },
          contactAddress: {
            line1: formData?.contactAddress?.line1 || null,
            line2: formData?.contactAddress?.line2 || null,
            line3: formData?.contactAddress?.line3 || null,
            locality: formData?.contactAddress?.locality || null,
            province: formData?.contactAddress?.province || null,
            postalCode: formData?.contactAddress?.postalCode || null,
            country: formData?.contactAddress?.country
              ? JSON.parse(formData?.contactAddress?.country)
              : { referenceID: null, text: null },
            provinceReferenceID:
              formData?.contactAddress?.provinceReferenceID ?? null,
            uniqueReference: formData?.contactAddress?.uniqueReference ?? null
          },
          legalName: legalName,
          policyholderType: formData.policyholderType,
          id: clientId,
          referenceID: formData.referenceID || client?.referenceID || null,
          ownerID: formData.ownerID || null
        })
        .then(goBack)
        .catch(() => {
          // Check response errors here and return relevant messages.
          notification.error({
            message: "Unable to update client, please try again."
          })
        })
    } else {
      notification.error({
        message: "Unable to submit form, missing required fields."
      })
    }
  }

  return (
    <Layout>
      <LayoutPageContent>
        <Spin spinning={clientData.isLoading}>
          <ClientForm
            onFinish={onFinish}
            client={client}
            isLoading={updatePolicyholder.isLoading}
            onBack={goBack}
            isEditMode={true}
          />
        </Spin>
      </LayoutPageContent>
    </Layout>
  )
}

type IUpdateClientProps = RouteComponentProps<
  {
    clientId: string
    subpage: string
  },
  StaticContext
>

export default withRouter(EditClient)
